import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Select, Skeleton } from '@mantine/core';
import { HiChevronDown } from 'react-icons/hi2';
import CountryFlag from '../blocks/newBlocks/arbk/CountryFlag';
import { getData } from '../../services/fetch-service';

const HUAJ_COLOR = '#4f46e5';
const FEMRA_COLOR = '#db2777';
const TOP20_COLOR = '#0d9488';
const DEFAULT_STATS_YEAR = '2026';
const TRACK_COLOR = '#eef1f6';
const BAR_MIN_WIDTH = 6;

function gjuhaId(locale) {
    if (locale === 'en') return 2;
    if (locale === 'sr') return 3;
    return 1;
}

const CountryItem = React.forwardRef(({ label, code, ...others }, ref) => (
    <div ref={ref} {...others}>
        <span className="tw-inline-flex tw-items-center tw-gap-2">
            <CountryFlag code={code} size={16} />
            {label}
        </span>
    </div>
));

function FilterSelect(props) {
    const {
        placeholder, value, onChange, data, clearVal, itemComponent, icon,
    } = props;
    return (
        <Select
            placeholder={placeholder}
            searchable
            clearable
            allowDeselect
            icon={icon}
            itemComponent={itemComponent}
            rightSection={value ? '' : <HiChevronDown size="1rem" />}
            rightSectionWidth={30}
            onChange={(v) => onChange(v === null ? clearVal : v)}
            value={value}
            styles={{ rightSection: { pointerEvents: 'none' } }}
            data={data}
        />
    );
}

FilterSelect.propTypes = {
    placeholder: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    data: PropTypes.array.isRequired,
    clearVal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    itemComponent: PropTypes.elementType,
    icon: PropTypes.node,
};

FilterSelect.defaultProps = {
    value: '',
    clearVal: '',
    itemComponent: undefined,
    icon: undefined,
};

function StatCard(props) {
    const { label, value } = props;
    return (
        <div className="tw-flex tw-flex-col tw-gap-1 tw-px-5 tw-py-4 tw-rounded-brand tw-border tw-border-solid tw-border-line tw-bg-paper tw-min-w-[180px]">
            <span className="tw-text-xs tw-uppercase tw-tracking-overline tw-text-muted tw-font-semibold">{label}</span>
            <span className="tw-text-2xl tw-font-bold tw-text-navy">{Number(value || 0).toLocaleString()}</span>
        </div>
    );
}

StatCard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

StatCard.defaultProps = {
    value: 0,
};

function BarList(props) {
    const {
        loader, items, color, emptyText,
    } = props;
    const [activeKey, setActiveKey] = useState(null);

    if (loader) {
        return (
            <div className="tw-flex tw-flex-col tw-gap-3 tw-py-5">
                <Skeleton height={22} radius="sm" />
                <Skeleton height={22} radius="sm" />
                <Skeleton height={22} radius="sm" />
                <Skeleton height={22} radius="sm" />
                <Skeleton height={22} radius="sm" />
                <Skeleton height={22} radius="sm" />
                <Skeleton height={22} radius="sm" />
                <Skeleton height={22} radius="sm" />
            </div>
        );
    }

    if (!items.length) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center tw-h-64 tw-text-muted tw-text-base">{emptyText}</div>
        );
    }

    const rows = [...items].sort((a, b) => b.value - a.value);
    const max = rows.reduce((acc, r) => Math.max(acc, r.value), 0) || 1;

    return (
        <div className="tw-flex tw-flex-col tw-gap-1 tw-py-2">
            {rows.map((row) => {
                const pct = Math.max(0, Math.min(100, (row.value / max) * 100));
                const key = `${row.code || ''}-${row.label}`;
                const isActive = activeKey === key;
                const toggle = () => setActiveKey(isActive ? null : key);
                return (
                    <div
                        key={key}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isActive}
                        title={row.label}
                        onClick={toggle}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggle();
                            }
                        }}
                        className={`tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-py-[5px] tw-px-2 tw-rounded-brand tw-cursor-pointer${isActive ? ' tw-bg-canvas' : ''}`}
                    >
                        <div
                            className={`tw-flex tw-items-center tw-gap-2 tw-min-w-0 ${isActive ? 'tw-w-full' : 'tw-w-[42%] tw-max-w-[340px]'}`}
                        >
                            {row.code ? <CountryFlag code={row.code} label={row.label} size={14} /> : null}
                            <span className={`tw-text-sm tw-text-navy ${isActive ? 'tw-whitespace-normal tw-break-words tw-font-semibold' : 'tw-truncate'}`}>
                                {row.label}
                            </span>
                        </div>
                        <div
                            className="tw-flex-1 tw-min-w-0 tw-h-2.5 tw-rounded-full tw-overflow-hidden"
                            style={{ backgroundColor: TRACK_COLOR }}
                        >
                            <div
                                className="tw-h-full tw-rounded-full"
                                style={{
                                    width: `${pct}%`,
                                    minWidth: row.value > 0 ? BAR_MIN_WIDTH : 0,
                                    backgroundColor: color,
                                }}
                            />
                        </div>
                        <span
                            className="tw-flex-shrink-0 tw-w-16 md:tw-w-24 tw-text-right tw-text-sm tw-font-semibold tw-text-navy"
                            style={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                            {row.value.toLocaleString()}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

BarList.propTypes = {
    loader: PropTypes.bool.isRequired,
    items: PropTypes.array.isRequired,
    color: PropTypes.string.isRequired,
    emptyText: PropTypes.string.isRequired,
};

function OpenDataStats(props) {
    const { locale, L } = props;
    const S = L.stats;

    const [komunat, setKomunat] = useState([]);
    const [shtetet, setShtetet] = useState([]);
    const [sektoret, setSektoret] = useState([]);
    const [llojet, setLlojet] = useState([]);

    const [hViti, setHViti] = useState(DEFAULT_STATS_YEAR);
    const [hStatusi, setHStatusi] = useState('');
    const [hKomuna, setHKomuna] = useState('');
    const [hShteti, setHShteti] = useState('');
    const [hSektori, setHSektori] = useState('');
    const [hLloji, setHLloji] = useState('');
    const [huaj, setHuaj] = useState({});
    const [huajLoader, setHuajLoader] = useState(true);

    const [fViti, setFViti] = useState(DEFAULT_STATS_YEAR);
    const [fStatusi, setFStatusi] = useState('');
    const [fKomuna, setFKomuna] = useState('');
    const [fSektori, setFSektori] = useState('');
    const [fLloji, setFLloji] = useState('');
    const [femra, setFemra] = useState({});
    const [femraLoader, setFemraLoader] = useState(true);

    const [tViti, setTViti] = useState(DEFAULT_STATS_YEAR);
    const [tKomuna, setTKomuna] = useState('');
    const [top20, setTop20] = useState({});
    const [top20Loader, setTop20Loader] = useState(true);

    const years = useMemo(() => {
        const cy = new Date().getFullYear();
        const arr = [];
        for (let i = 0; i < 25; i++) {
            arr.push({ value: String(cy - i), label: String(cy - i) });
        }
        return arr;
    }, []);

    const statusData = useMemo(() => ([
        { value: '1', label: S.statusAktive },
        { value: '2', label: S.statusShuar },
        { value: '3', label: S.statusPasive },
    ]), [S]);

    useEffect(() => {
        getData(`Services/ListaKomunave?Gjuha=${locale}`).then((r) => setKomunat(r.data || [])).catch(() => {});
        getData(`Services/GetSektori?Gjuha=${locale}`).then((r) => setSektoret(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        getData(`Services/LlojetEBiznesit?Gjuha=${locale}`).then((r) => setLlojet(r.data || [])).catch(() => {});
        getData(`Services/ListaShteteve?GjuhaID=${gjuhaId(locale)}`)
            .then((r) => setShtetet(r.data || []))
            .catch(() => {
                getData(`Services/ListaShteteve?Gjuha=${locale}`).then((r) => setShtetet(r.data || [])).catch(() => {});
            });
    }, [locale]);

    const komunatData = useMemo(() => komunat
        .map((i) => ({ value: String(i.id || i.ID || i.KomunaID || ''), label: i.pershkrimi || i.Pershkrimi || i.emri || '' }))
        .filter((i) => i.value), [komunat]);

    const shtetetData = useMemo(() => shtetet
        .map((i) => ({
            value: String(i.id || i.ID || i.ShtetiID || ''),
            label: i.pershkrimi || i.Pershkrimi || i.emri || '',
            code: i.code || i.Code || '',
        }))
        .filter((i) => i.value), [shtetet]);

    const sektoretData = useMemo(() => (Array.isArray(sektoret) ? sektoret : [])
        .map((i) => ({ value: String(i.Id ?? i.id ?? ''), label: i.Pershkrimi || i.pershkrimi || '' }))
        .filter((i) => i.value), [sektoret]);

    const llojetData = useMemo(() => llojet
        .map((i) => ({ value: String(i.LlojiBiznesitID || ''), label: i.Pershkrimi || '' }))
        .filter((i) => i.value), [llojet]);

    const shtetetMap = useMemo(() => {
        const m = {};
        shtetetData.forEach((i) => { m[i.value] = i.label; });
        return m;
    }, [shtetetData]);

    const shtetetCodeMap = useMemo(() => {
        const m = {};
        shtetet.forEach((i) => {
            const id = String(i.id || i.ID || i.ShtetiID || '');
            const code = i.code || i.Code || '';
            if (id && code) m[id] = code;
        });
        return m;
    }, [shtetet]);

    useEffect(() => {
        let ignore = false;
        setHuajLoader(true);
        getData(`Services/StatPronariHuaj?Viti=${hViti || 0}&Statusi=${hStatusi || 0}&KomunaId=${hKomuna || 0}&ShtetiId=${hShteti || 0}&Sektori=${hSektori || ''}&LlojiBiznesitID=${hLloji || 0}`)
            .then((r) => {
                if (!ignore) {
                    setHuaj(r.data || {});
                    setHuajLoader(false);
                }
            })
            .catch(() => { if (!ignore) setHuajLoader(false); });
        return () => { ignore = true; };
    }, [hViti, hStatusi, hKomuna, hShteti, hSektori, hLloji]);

    useEffect(() => {
        let ignore = false;
        setFemraLoader(true);
        getData(`Services/StatPronariFemra?Viti=${fViti || 0}&Statusi=${fStatusi || 0}&KomunaId=${fKomuna || 0}&Sektori=${fSektori || ''}&LlojiBiznesitID=${fLloji || 0}`)
            .then((r) => {
                if (!ignore) {
                    setFemra(r.data || {});
                    setFemraLoader(false);
                }
            })
            .catch(() => { if (!ignore) setFemraLoader(false); });
        return () => { ignore = true; };
    }, [fViti, fStatusi, fKomuna, fSektori, fLloji]);

    useEffect(() => {
        let ignore = false;
        setTop20Loader(true);
        getData(`Services/StatTop20Aktivitetet?Viti=${tViti || 0}&KomunaId=${tKomuna || 0}&Gjuha=${locale}`)
            .then((r) => {
                if (!ignore) {
                    setTop20(r.data || {});
                    setTop20Loader(false);
                }
            })
            .catch(() => { if (!ignore) setTop20Loader(false); });
        return () => { ignore = true; };
    }, [tViti, tKomuna, locale]);

    const huajItems = (Array.isArray(huaj.Lista) ? huaj.Lista : []).map((i) => {
        const id = String(i.Pershkrimi);
        return { label: shtetetMap[id] || i.Pershkrimi, value: Number(i.NR) || 0, code: shtetetCodeMap[id] || '' };
    });

    const femraItems = (Array.isArray(femra.Lista) ? femra.Lista : [])
        .map((i) => ({ label: i.Pershkrimi, value: Number(i.NR) || 0 }));

    const top20Items = (Array.isArray(top20.Lista) ? top20.Lista : [])
        .map((i) => ({ label: i.Pershkrimi, value: Number(i.NR) || 0 }));

    return (
        <div className="OpenData-stats tw-font-inter">
            <section className="full-margin tw-mb-16">
                <div className="tw-rounded-brand-lg tw-border tw-border-solid tw-border-line tw-bg-paper tw-shadow-card tw-p-6 md:tw-p-8">
                    <h2 className="tw-text-2xl tw-font-bold tw-text-navy tw-mb-1">{S.huajTitle}</h2>
                    <p className="tw-text-sm tw-text-muted tw-mb-5">{S.huajChart}</p>

                    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-3 tw-mb-6">
                        <FilterSelect placeholder={S.fYear} value={hViti} onChange={setHViti} data={years} />
                        <FilterSelect placeholder={S.fStatus} value={hStatusi} onChange={setHStatusi} data={statusData} />
                        <FilterSelect placeholder={S.fKomuna} value={hKomuna} onChange={setHKomuna} data={komunatData} />
                        <FilterSelect
                            placeholder={S.fShteti}
                            value={hShteti}
                            onChange={setHShteti}
                            data={shtetetData}
                            itemComponent={CountryItem}
                            icon={shtetetCodeMap[String(hShteti)]
                                ? <CountryFlag code={shtetetCodeMap[String(hShteti)]} size={16} />
                                : undefined}
                        />
                        <FilterSelect placeholder={S.fSektori} value={hSektori} onChange={setHSektori} data={sektoretData} />
                        <FilterSelect placeholder={S.fLloji} value={hLloji} onChange={setHLloji} data={llojetData} />
                    </div>

                    <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mb-6">
                        <StatCard label={S.numri} value={huaj.Numri} />
                        <StatCard label={S.totali} value={huaj.Totali} />
                    </div>

                    <BarList
                        loader={huajLoader}
                        items={huajItems}
                        color={HUAJ_COLOR}
                        emptyText={S.noData}
                    />
                </div>
            </section>

            <section className="full-margin tw-mb-12">
                <div className="tw-rounded-brand-lg tw-border tw-border-solid tw-border-line tw-bg-paper tw-shadow-card tw-p-6 md:tw-p-8">
                    <h2 className="tw-text-2xl tw-font-bold tw-text-navy tw-mb-1">{S.femraTitle}</h2>
                    <p className="tw-text-sm tw-text-muted tw-mb-5">{S.femraChart}</p>

                    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-3 tw-mb-6">
                        <FilterSelect placeholder={S.fYear} value={fViti} onChange={setFViti} data={years} />
                        <FilterSelect placeholder={S.fStatus} value={fStatusi} onChange={setFStatusi} data={statusData} />
                        <FilterSelect placeholder={S.fKomuna} value={fKomuna} onChange={setFKomuna} data={komunatData} />
                        <FilterSelect placeholder={S.fSektori} value={fSektori} onChange={setFSektori} data={sektoretData} />
                        <FilterSelect placeholder={S.fLloji} value={fLloji} onChange={setFLloji} data={llojetData} clearVal={0} />
                    </div>

                    <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mb-6">
                        <StatCard label={S.numri} value={femra.Numri} />
                        <StatCard label={S.totali} value={femra.Totali} />
                    </div>

                    <BarList
                        loader={femraLoader}
                        items={femraItems}
                        color={FEMRA_COLOR}
                        emptyText={S.noData}
                    />
                </div>
            </section>

            <section className="full-margin tw-mb-12">
                <div className="tw-rounded-brand-lg tw-border tw-border-solid tw-border-line tw-bg-paper tw-shadow-card tw-p-6 md:tw-p-8">
                    <h2 className="tw-text-2xl tw-font-bold tw-text-navy tw-mb-1">{S.top20Title}</h2>
                    <p className="tw-text-sm tw-text-muted tw-mb-5">{S.top20Chart}</p>

                    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-3 tw-mb-6">
                        <FilterSelect placeholder={S.fYear} value={tViti} onChange={setTViti} data={years} />
                        <FilterSelect placeholder={S.fKomuna} value={tKomuna} onChange={setTKomuna} data={komunatData} />
                    </div>

                    <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mb-6">
                        <StatCard label={S.totali} value={top20.Totali} />
                    </div>

                    <BarList
                        loader={top20Loader}
                        items={top20Items}
                        color={TOP20_COLOR}
                        emptyText={S.noData}
                    />
                </div>
            </section>
        </div>
    );
}

OpenDataStats.propTypes = {
    locale: PropTypes.string.isRequired,
    L: PropTypes.object.isRequired,
};

export default OpenDataStats;
